import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ParticipationModeSubType } from '@seen/core/models/communications/constants'
import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Signature } from '@seen/core/models/signature'
import { getReferenceString } from '@seen/core/references'
import { orderBy, sortBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { FaxItem } from '#src/pages/participants/timeline/FaxItem.tsx'

export function DocumentReferenceActivity({
  documentReference,
}: {
  documentReference: ExtendedDocumentReference
}): JSX.Element {
  const { data: pastDocRefs = [] } = useResourceVersions(
    ExtendedDocumentReference,
    documentReference.id
  )

  const sortedDocRefs = sortBy(
    pastDocRefs,
    (d: ExtendedDocumentReference) => d.lastUpdated
  )

  const documentActivity = sortedDocRefs.reduce<
    {
      action: ReactNode
      documentReference: ExtendedDocumentReference
      key?: string
    }[]
  >((acc, curr, idx) => {
    if (acc.length === 0) {
      return [
        {
          action: 'document created',
          documentReference: curr,
          key: `${curr.versionId}-created`,
        },
      ]
    }

    const prev = sortedDocRefs[idx - 1]

    // Track category changes
    const prevCategories = prev?.categories ?? []
    const currCategories = curr.categories ?? []
    if (JSON.stringify(prevCategories) !== JSON.stringify(currCategories)) {
      const added = currCategories.filter((c) => !prevCategories.includes(c))
      const removed = prevCategories.filter((c) => !currCategories.includes(c))

      if (added.length > 0) {
        acc.push({
          action: `added ${added.length === 1 ? 'category' : 'categories'}: ${added.join(', ')}`,
          documentReference: curr,
          key: `${curr.versionId}-categories-added`,
        })
        return acc
      }

      if (removed.length > 0) {
        acc.push({
          action: `removed ${removed.length === 1 ? 'category' : 'categories'}: ${removed.join(', ')}`,
          documentReference: curr,
          key: `${curr.versionId}-categories-removed`,
        })
        return acc
      }
    }

    // Track type changes
    if (curr.typeCode !== prev?.typeCode) {
      if (curr.typeCode && !prev?.typeCode) {
        acc.push({
          action: `set document type: ${curr.typeCode}`,
          documentReference: curr,
          key: `${curr.versionId}-type-added`,
        })
        return acc
      }

      if (!curr.typeCode && prev?.typeCode) {
        acc.push({
          action: `removed document type: ${prev.typeCode}`,
          documentReference: curr,
          key: `${curr.versionId}-type-removed`,
        })
        return acc
      }

      if (curr.typeCode && prev?.typeCode) {
        acc.push({
          action: `changed document type: ${prev.typeCode} → ${curr.typeCode}`,
          documentReference: curr,
          key: `${curr.versionId}-type-changed`,
        })
        return acc
      }
    }

    // Track folder changes
    if (curr.folder?.id !== prev?.folder?.id) {
      if (curr.folder && !prev?.folder) {
        acc.push({
          action: `moved to folder: ${curr.folder.name}`,
          documentReference: curr,
          key: `${curr.versionId}-folder-added`,
        })
        return acc
      }

      if (!curr.folder && prev?.folder) {
        acc.push({
          action: `removed from folder: ${prev.folder.name}`,
          documentReference: curr,
          key: `${curr.versionId}-folder-removed`,
        })
        return acc
      }

      if (curr.folder && prev?.folder) {
        acc.push({
          action: `moved from ${prev.folder.name} → ${curr.folder.name}`,
          documentReference: curr,
          key: `${curr.versionId}-folder-changed`,
        })
        return acc
      }
    }

    // Track order linking changes
    const prevOrderRefs = prev?.orders?.map(getReferenceString) ?? []
    const currOrderRefs = curr.orders?.map(getReferenceString) ?? []
    if (JSON.stringify(prevOrderRefs) !== JSON.stringify(currOrderRefs)) {
      const added = currOrderRefs.filter((o) => !prevOrderRefs.includes(o))
      const removed = prevOrderRefs.filter((o) => !currOrderRefs.includes(o))

      if (added.length > 0) {
        acc.push({
          action: `linked ${added.length === 1 ? 'order' : 'orders'}`,
          documentReference: curr,
          key: `${curr.versionId}-orders-added`,
        })
        return acc
      }

      if (removed.length > 0) {
        acc.push({
          action: `unlinked ${removed.length === 1 ? 'order' : 'orders'}`,
          documentReference: curr,
          key: `${curr.versionId}-orders-removed`,
        })
        return acc
      }

      return acc
    }

    // Track participant/subject changes
    const currSubjectRef = curr.subject
      ? getReferenceString(curr.subject)
      : null
    const prevSubjectRef = prev?.subject
      ? getReferenceString(prev.subject)
      : null

    if (currSubjectRef !== prevSubjectRef) {
      if (currSubjectRef && !prevSubjectRef) {
        acc.push({
          action: 'linked participant',
          documentReference: curr,
          key: `${curr.versionId}-subject-added`,
        })
        return acc
      }

      if (!currSubjectRef && prevSubjectRef) {
        acc.push({
          action: 'unlinked participant',
          documentReference: curr,
          key: `${curr.versionId}-subject-removed`,
        })
        return acc
      }

      if (currSubjectRef && prevSubjectRef) {
        acc.push({
          action: 'changed participant',
          documentReference: curr,
          key: `${curr.versionId}-subject-changed`,
        })
        return acc
      }
    }

    return acc
  }, [])

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([documentReference.reference])
  )

  const { data: signatures = [] } = useSearch(
    Signature,
    Signature.queryByTarget(documentReference.reference.reference)
  )

  const signatureActivity = signatures
    .filter((sig) => !sig.voided)
    .map((sig) => ({
      action: 'signed',
      signature: sig,
      key: `${sig.id}-signature`,
    }))

  const { data: relatedFaxes = [] } = useSearch(ExtendedCommunication, {
    _sort: '-_lastUpdated',
    _count: '1000',
    'status:not': 'entered-in-error',
    medium: ParticipationModeSubType.FAX,
    'based-on': documentReference.reference,
  })

  const faxActivity = relatedFaxes.map((fax) => ({
    fax,
    key: `${fax.id}-fax`,
  }))

  const all = orderBy(
    [...documentActivity, ...comments, ...signatureActivity, ...faxActivity],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      if ('fax' in a) {
        return a.fax.sent
      }
      if ('signature' in a) {
        return a.signature.recorded
      }
      return a.documentReference.lastUpdated
    },
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment
              key={entry.reference.reference}
              comment={entry}
              showRelated={
                !entry.partOf
                  ?.map(getReferenceString)
                  .some((r) => r === documentReference.reference.reference)
              }
            />
          )
        }

        if ('fax' in entry) {
          return <FaxItem key={entry.key} fax={entry.fax} />
        }

        if ('signature' in entry) {
          return (
            <ActivityEntry
              key={entry.key}
              author={entry.signature.who}
              time={entry.signature.recorded}
              action={entry.action}
            />
          )
        }

        return (
          <ActivityEntry
            key={entry.key ?? entry.documentReference.versionId}
            author={entry.documentReference.lastUpdatedBy}
            time={entry.documentReference.lastUpdated}
            action={entry.action}
          />
        )
      })}
    </>
  )
}
